import React, { useEffect, useState } from "react";
import { api, money, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { InlineLoader, EmptyState } from "@/components/shared";
import { Users, Repeat, Clock, Crown, Mail, Send, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";

const SEGMENTS = [
  { id: "all", label: "All donors" },
  { id: "recurring", label: "Recurring" },
  { id: "major", label: "Major ($500+)" },
  { id: "lapsed", label: "Lapsed" },
];

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); } catch { return "—"; } };

export default function DonorCRM() {
  const [segment, setSegment] = useState("all");
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [reengaging, setReengaging] = useState(false);

  const load = (seg) => {
    setData(null);
    api.get("/pro/donors", { params: { segment: seg } })
      .then((r) => { setData(r.data.donors); setSummary(r.data.summary); })
      .catch(() => { setData([]); });
  };
  useEffect(() => { load(segment); }, [segment]);

  const reengage = async () => {
    setReengaging(true);
    try {
      const { data: res } = await api.post("/pro/donors/reengage");
      toast.success(res.emailed > 0 ? `Re-engagement sent to ${res.emailed} lapsed donor${res.emailed !== 1 ? "s" : ""}.` : "No lapsed donors to re-engage right now.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setReengaging(false); }
  };

  return (
    <div className="rounded-2xl border border-ff-border bg-white p-6 shadow-sm" data-testid="donor-crm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 font-heading text-lg font-extrabold text-ff-blue"><Users className="h-5 w-5 text-ff-action" /> Donor CRM</h3>
          <p className="mt-0.5 text-sm text-ff-medium">Your supporters across all campaigns — segment, track, and re-engage.</p>
        </div>
        <Button onClick={reengage} disabled={reengaging} data-testid="crm-reengage" className="bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
          {reengaging ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</> : <><Mail className="mr-2 h-4 w-4" /> Re-engage lapsed</>}
        </Button>
      </div>

      {summary && (
        <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="crm-summary">
          <Stat label="Total donors" value={summary.total_donors} icon={Users} />
          <Stat label="Total raised" value={money(summary.total_raised)} icon={Crown} />
          <Stat label="Avg gift" value={money(summary.avg_gift)} icon={Repeat} />
          <Stat label="Lapsed" value={summary.lapsed} icon={Clock} accent />
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        {SEGMENTS.map((s) => (
          <button key={s.id} onClick={() => setSegment(s.id)} data-testid={`crm-seg-${s.id}`}
            className={`rounded-full border px-3.5 py-1.5 text-xs font-bold transition-colors duration-200 ${segment === s.id ? "border-ff-blue bg-ff-blue text-white" : "border-ff-border text-ff-blue hover:border-ff-blue"}`}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="mt-4">
        {data === null ? <InlineLoader label="Loading donors" /> : data.length === 0 ? (
          <EmptyState icon={Inbox} title="No donors in this segment" message="Try another segment or share your campaign to grow your list." testid="crm-empty" />
        ) : (
          <div className="overflow-x-auto" data-testid="crm-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ff-border text-left text-xs uppercase tracking-wide text-ff-medium">
                  <th className="py-2 pr-3">Donor</th><th className="py-2 pr-3">Total</th><th className="py-2 pr-3">Gifts</th><th className="py-2 pr-3">Last gift</th><th className="py-2">Tags</th>
                </tr>
              </thead>
              <tbody>
                {data.map((d) => (
                  <tr key={d.email} className="border-b border-ff-border/50" data-testid={`crm-row-${d.email}`}>
                    <td className="py-2.5 pr-3"><div className="font-semibold text-ff-blue">{d.name}</div><div className="text-xs text-ff-medium">{d.email}</div></td>
                    <td className="py-2.5 pr-3 font-bold text-ff-green">{money(d.total_given)}</td>
                    <td className="py-2.5 pr-3 text-ff-dark">{d.gift_count}</td>
                    <td className="py-2.5 pr-3 text-ff-medium">{fmtDate(d.last_gift)}</td>
                    <td className="py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {d.recurring_active && <Tag className="bg-[#ECFDF5] text-ff-green"><Repeat className="h-3 w-3" /> Recurring</Tag>}
                        {d.is_major && <Tag className="bg-[#FFF7ED] text-ff-amber"><Crown className="h-3 w-3" /> Major</Tag>}
                        {d.is_lapsed && <Tag className="bg-[#FEF2F2] text-ff-red"><Clock className="h-3 w-3" /> Lapsed</Tag>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "border-ff-red/30 bg-[#FEF2F2]" : "border-ff-border bg-[#F9FAFB]"}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-ff-medium"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="mt-1 font-heading text-xl font-extrabold text-ff-blue">{value}</div>
    </div>
  );
}

function Tag({ children, className }) {
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${className}`}>{children}</span>;
}
